import { Plus } from 'lucide-react';
import type { ToolLookupFilter } from '../../lib/api';
import { LookupFilterRow } from './LookupFilterRow';
import {
  LOOKUP_MAX_FILTERS,
  defaultLookupFilterValue,
  type LookupSchemaField,
} from './lookup-tool-model';
import styles from './LookupTool.module.css';

interface LookupFilterEditorProps {
  fields: LookupSchemaField[];
  filters: ToolLookupFilter[];
  onChange: (filters: ToolLookupFilter[]) => void;
}

/** Edits the fixed equality filters stored on a lookup tool's configuration. */
export const LookupFilterEditor = ({ fields, filters, onChange }: LookupFilterEditorProps) => {
  const selectedFields = new Set(filters.map(filter => filter.field));
  const firstAvailableField = fields.find(field => !selectedFields.has(field.path));
  const filterCountLabel = `${filters.length} of ${LOOKUP_MAX_FILTERS} filters`;

  const addFilter = () => {
    if (!firstAvailableField) return;
    onChange([
      ...filters,
      { field: firstAvailableField.path, value: defaultLookupFilterValue(firstAvailableField) },
    ]);
  };

  return (
    <fieldset className={styles.filterEditor}>
      <legend>Fixed filters</legend>
      <p>
        Every row is joined with AND and uses equality. These values are fixed configuration; the agent
        cannot supply or change them.
      </p>
      {filters.length > 0 && (
        <ol>
          {filters.map((filter, index) => (
            <LookupFilterRow
              key={`${filter.field}-${index}`}
              filter={filter}
              fields={fields}
              selectedFieldPaths={selectedFields}
              onReplace={nextFilter =>
                onChange(filters.map((row, rowIndex) => (rowIndex === index ? nextFilter : row)))
              }
              onRemove={() => onChange(filters.filter((_, rowIndex) => rowIndex !== index))}
            />
          ))}
        </ol>
      )}
      <button
        type="button"
        className={styles.addFilterButton}
        disabled={filters.length >= LOOKUP_MAX_FILTERS || !firstAvailableField}
        onClick={addFilter}>
        <Plus />
        Add filter
      </button>
      <small>{filterCountLabel}</small>
    </fieldset>
  );
};
